import type { Patch } from "@freightsimple/shared/types";
import type { LineItem } from "@freightsimple/generated-dashboard-openapi-client/models/LineItem";
import { useEffect, useState } from "react";
import type { AddLineItemType } from "@/Shared/Types/lineItemTypes";
import { useNmfcDescription } from "@/Slices/nmfcDescriptionCacheSlice";
import { useLineItemsLookupNmfcCode } from "./useLineItemsLookupNmfcCode";

interface NmfcCoordinationParams {
  lineItem: AddLineItemType;
  onChange: (value: Patch<LineItem>) => void;
  /** When false the NMFC input isn't rendered, so dimensions drive the class. */
  shouldRenderFreightClass: boolean;
  setHasNmfcError: (hasError: boolean) => void;
}

/**
 * Keeps the NMFC code and the freight class in step as the customer types.
 *
 * Freight class comes from one of two places — an NMFC code, or the density
 * implied by the dimensions — so editing either one has to reconcile both. Two
 * behaviours are worth knowing about:
 *
 * A dimension edit only re-looks-up when an NMFC code is already set, and it
 * waits two seconds first, because dimensions are typed a digit at a time and
 * every intermediate value would otherwise be a wasted round trip against a
 * half-entered number.
 *
 * With the NMFC input hidden, a dimension edit clears the code outright rather
 * than looking it up — an invisible code the customer can't see or correct
 * would silently pin the class.
 */
export function useLineItemsNmfcCoordination(params: NmfcCoordinationParams) {
  const { lineItem, onChange, shouldRenderFreightClass, setHasNmfcError } =
    params;
  const [timeoutId, setTimeoutId] = useState<NodeJS.Timeout>();

  const {
    nmfcLookupInProgress,
    lookupNmfcCode,
    nmfcErrorMessage,
    clearNmfcError,
    nmfcSubItems,
  } = useLineItemsLookupNmfcCode();

  const nmfcDescription = useNmfcDescription(lineItem.nmfcItemNumber);

  useEffect(
    function () {
      setHasNmfcError(nmfcErrorMessage !== undefined);
    },

    [nmfcErrorMessage],
  );

  async function onNmfcChange(value: Patch<LineItem>) {
    const newNmfcCode = value.nmfcItemNumber;
    if (newNmfcCode) {
      const response = await lookupNmfcCode({
        ...lineItem,
        ...value,
        nmfcItemNumber: newNmfcCode,
      });

      if (response) {
        onChange({
          freightClass: response.freightClass,
          nmfcItemNumber: response.nmfcWithSubItem,
        });
      }
      // If validation fails, don't update the NMFC code
    } else {
      onChange({
        freightClass: undefined,
        nmfcItemNumber: undefined,
      });
    }
  }

  async function onNmfcChangeDelayed(value: Patch<LineItem>) {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }

    const newId = setTimeout(function () {
      onNmfcChange(value);
    }, 2000);

    setTimeoutId(newId);
  }

  function onDimensionsChange(value: Patch<LineItem>) {
    if (!shouldRenderFreightClass) {
      onChange({ ...value, nmfcItemNumber: undefined });
      return;
    }

    if (lineItem.nmfcItemNumber !== undefined) {
      onNmfcChangeDelayed({ ...lineItem, ...value });
    }

    onChange(value);
  }

  return {
    nmfcLookupInProgress,
    nmfcErrorMessage,
    clearNmfcError,
    nmfcSubItems,
    nmfcDescription,
    onNmfcChange,
    onDimensionsChange,
  };
}
